import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import useSchool, { BRAND_COLOR_FALLBACK } from '@/hooks/useSchool';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, FileText, Clock, CheckCircle2, Hourglass, Loader2, Sparkles } from 'lucide-react';

const STATUS_PILL = {
  not_started: { label: 'Not started', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
  in_progress: { label: 'In progress', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
  submitted: { label: 'Awaiting teacher', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  released: { label: 'Result available', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
};

export default function StudentAssessments() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [missionCountByAid, setMissionCountByAid] = useState({});
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [child, setChild] = useState(null);
  const school = useSchool(child?.school_id);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, mRes] = await Promise.all([
        api.get('/me/assessments'),
        api.get('/me/missions').catch((err) => {
          console.warn('[StudentAssessments] missions fetch failed:', err);
          return { data: { missions: [] } };
        }),
      ]);
      setItems(aRes.data || []);
      const counts = {};
      for (const m of (mRes.data?.missions || [])) {
        const aid = m?.source?.assessment_id;
        if (!aid) continue;
        counts[aid] = (counts[aid] || 0) + 1;
      }
      setMissionCountByAid(counts);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load assessments');
      setItems([]);
      setMissionCountByAid({});
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const meRes = await api.get('/auth/me');
        setMe(meRes.data);
        if (meRes.data?.child_id) {
          const cRes = await api.get(`/children/${meRes.data.child_id}`);
          setChild(cRes.data);
        }
      } catch (err) {
        console.warn('[StudentAssessments] child fetch failed:', err);
      }
    })();
    load();
  }, [load]);

  const brandColor = school?.brand_color || BRAND_COLOR_FALLBACK;

  const goHome = () => {
    if (child?.child_id) navigate(`/child/${child.child_id}/home`);
    else navigate('/');
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50"
      data-testid="student-assessments"
    >
      <header
        className="border-b bg-white/90 backdrop-blur sticky top-0 z-10"
        style={{ borderTopWidth: 4, borderTopColor: brandColor }}
      >
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={goHome} data-testid="back-home-btn">
            <ArrowLeft size={16} className="mr-1.5" /> Home
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-slate-900">Assessments</h1>
            <p className="text-xs text-slate-500">{me?.email}</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-indigo-600" /></div>
        ) : items.length === 0 ? (
          <div className="border-2 border-dashed border-slate-200 rounded-2xl py-16 px-6 text-center bg-white/60" data-testid="empty-assessments">
            <div className="mx-auto w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
              <FileText size={26} className="text-indigo-500" />
            </div>
            <p className="text-slate-900 font-semibold text-lg">No assessments yet</p>
            <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
              Your teacher hasn't published anything yet. Check back later!
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((a) => (
              <AssessmentRow
                key={a.assessment_id}
                a={a}
                brandColor={brandColor}
                missionCount={missionCountByAid[a.assessment_id] || 0}
                onStart={() => navigate(`/student/assessments/${a.assessment_id}/attempt`)}
                onResume={() => navigate(`/student/assessments/${a.assessment_id}/attempt`)}
                onResult={() => a.attempt_id && navigate(`/student/results/${a.attempt_id}`)}
                onImprove={() => navigate(`/student/missions?assessment_id=${a.assessment_id}`)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function AssessmentRow({ a, brandColor, missionCount, onStart, onResume, onResult, onImprove }) {
  const pill = STATUS_PILL[a.student_status] || STATUS_PILL.not_started;
  const due = a.due_at ? new Date(a.due_at) : null;
  const isOverdue = !!a.is_overdue;

  let primary = null;
  if (a.student_status === 'released') {
    primary = <Button onClick={onResult} className="text-white whitespace-nowrap shrink-0" style={{ backgroundColor: brandColor }} data-testid={`result-btn-${a.assessment_id}`}>
      <CheckCircle2 size={14} className="mr-1.5" /> View result
    </Button>;
  } else if (a.student_status === 'submitted') {
    primary = <Button variant="outline" onClick={onResult} disabled className="whitespace-nowrap shrink-0" data-testid={`waiting-btn-${a.assessment_id}`}>
      <Hourglass size={14} className="mr-1.5" /> Awaiting teacher
    </Button>;
  } else if (a.student_status === 'in_progress') {
    primary = <Button onClick={onResume} className="text-white whitespace-nowrap shrink-0" style={{ backgroundColor: brandColor }} disabled={isOverdue} data-testid={`resume-btn-${a.assessment_id}`}>
      Resume
    </Button>;
  } else {
    primary = <Button onClick={onStart} className="text-white whitespace-nowrap shrink-0" style={{ backgroundColor: brandColor }} disabled={isOverdue} data-testid={`start-btn-${a.assessment_id}`}>
      Start
    </Button>;
  }

  const showImprove = missionCount > 0;

  return (
    <div className="bg-white border rounded-xl p-4 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid={`assessment-${a.assessment_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h3 className="font-semibold text-slate-900 truncate min-w-0">{a.title}</h3>
          <Badge variant="outline" className={`${pill.cls} shrink-0 whitespace-nowrap`}>{pill.label}</Badge>
          {isOverdue && a.student_status !== 'released' && a.student_status !== 'submitted' && (
            <Badge variant="outline" className="bg-rose-100 text-rose-700 border-rose-300 shrink-0 whitespace-nowrap">Overdue</Badge>
          )}
        </div>
        <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
          {a.subject?.name && <span style={{ color: a.subject.color }}>{a.subject.name}</span>}
          {due && (
            <span className="flex items-center gap-1 whitespace-nowrap"><Clock size={11} /> Due {due.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}</span>
          )}
          {a.score_text && <span className="text-emerald-700 font-medium whitespace-nowrap">Score: {a.score_text}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap shrink-0">
        {showImprove && (
          <Button
            onClick={onImprove}
            className="text-white whitespace-nowrap shrink-0"
            style={{ backgroundColor: brandColor }}
            data-testid={`improve-btn-${a.assessment_id}`}
            title={`${missionCount} topic${missionCount === 1 ? '' : 's'} to improve from this assessment`}
          >
            <Sparkles size={14} className="mr-1.5" />
            Topics to improve
          </Button>
        )}
        {primary}
      </div>
    </div>
  );
}
